import restClient, { RestRequestOptions, RestResponse } from '.';
import { RegionsCode } from '../../constants/region';
import { Uuid } from '../../models/Base';
import { SpaceJson } from '../../models/SpaceStore/Space';
import {
  AccountJson,
  AllowanceJson,
  ProfileJson,
  UserJson,
} from '../../models/UserStore';
import {
  InvitationLinkJson,
  RedemptionCodeJson,
} from '../../models/UserStore/Invitation';
import { NameCardJson } from '../../models/UserStore/NameCard';
import { Optional, PartialPick } from '../../models/Utils';
import { WithProfilesData, WithProfilesParams } from '../utils';
import { ExtraData } from './post';

export interface UpdateCurrentUserBody {
  user: {
    username?: string;
    email?: string;
    name?: string;
    region?: RegionsCode;
    password?: string;
  };
  password?: string;
}

export interface CurrentUserResponse {
  user: UserJson;
}

export const getCurrentUser = (
  options?: RestRequestOptions,
): Promise<RestResponse<CurrentUserResponse>> => {
  return restClient.get('/user', options);
};

export const updateCurrentUser = (
  body: UpdateCurrentUserBody,
  options?: RestRequestOptions,
): Promise<RestResponse<CurrentUserResponse>> => {
  return restClient.patch('/user', body, options);
};

export interface AllowanceData {
  allowance: AllowanceJson;
}

export const getUserAllowance = (
  options?: RestRequestOptions,
): Promise<RestResponse<AllowanceData>> => {
  return restClient.get('/user/allowance', options);
};

export const logout = (
  body: PartialPick<AccountJson, 'settings' | 'usageData'> = {},
  options?: RestRequestOptions,
): Promise<RestResponse<unknown>> => {
  return restClient.post('/user/logout', body, options);
};

interface InvitationLinkData {
  invitationLink: InvitationLinkJson;
  data: ExtraData;
}

export const getInvitationLink = <P extends Optional<WithProfilesParams>>(
  path: { invitationLinkId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<InvitationLinkData & WithProfilesData<P>>> => {
  return restClient.get(
    `/user/invitation-links/${path.invitationLinkId}`,
    params,
    options,
  );
};

interface RedemptionCodeData {
  redemptionCode: RedemptionCodeJson;
  // data: ExtraData;
}

export const getRedemptionCode = <P extends Optional<WithProfilesParams>>(
  path: { redemptionCode: string },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<RedemptionCodeData & WithProfilesData<P>>> => {
  return restClient.get(
    `/user/redemption-codes/${path.redemptionCode}`,
    params,
    options,
  );
};

interface NameCardDataWithNull {
  nameCard: null | NameCardJson;
  profile?: ProfileJson;
  classroom?: SpaceJson;
  // gameSet?: GameSetJSON;
}

export const getNameCard = (
  path: { nameCardId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<NameCardDataWithNull>> => {
  return restClient.get(`/user/name-cards/${path.nameCardId}`, {}, options);
};

export interface CollectedNameCardData {
  collectedAt?: number;
  nameCard: NameCardJson;
  password?: string;
}

export const collectNameCard = (
  path: { nameCardId: Uuid },
  body: { email: string; userCode?: string },
  options?: RestRequestOptions,
): Promise<RestResponse<CollectedNameCardData>> => {
  return restClient.post(
    `/user/name-cards/${path.nameCardId}/collection`,
    body,
    options,
  );
};

export interface NameCardData {
  nameCard: NameCardJson;
  password?: string;
}

export const acceptNameCardTransfer = (
  path: { nameCardId: Uuid },
  body: { email: string; userCode?: string },
  options?: RestRequestOptions,
): Promise<RestResponse<NameCardData>> => {
  return restClient.post(
    `/user/name-cards/${path.nameCardId}/transfer-response`,
    { isRequestApproved: true, ...body },
    options,
  );
};
